import { isPrimitiveValue, mString } from 'lib/helpers/utility';

import { GAP, TABLE_FONT_SIZE } from './constants';

const NO_LINEBREAK_COLUMNS = ['id', 'date'];
const MAX_COLUMNS_PER_PAGE = 10;
export const CELL_LENGTH_MULTIPLE = 2.5;

//sum the data by column
export const sumByColumn = (data) => {
  if (!data.length) return [];

  return data[0].map((_, colIndex) => {
    const allNumberValues = data.filter((row) => typeof row[colIndex] === 'number');
    if (!allNumberValues.length) return '';

    return mString(allNumberValues.reduce((sum, row) => sum + row[colIndex], 0));
  });
};

export const filterColumns = (columns) => {
  return columns.filter((col) => col.field && col.field !== 'actions' && !col.hide);
};

const getValueLength = (value) => {
  return isPrimitiveValue(value) ? String(value || '').length : 1;
};

const getColumnMaxWidth = (rows, columnIndex) => {
  const maxLength = Math.max(
    ...rows.map((row) => {
      const value = row[columnIndex];
      return getValueLength(value);
    })
  );

  return maxLength * CELL_LENGTH_MULTIPLE; // Adjust multiplier based on font size (~5px per char)
};

export const getColumnStyles = (columnHeaders, rows) => {
  const maxColWidth = Math.max(...columnHeaders.map((col) => getValueLength(col)));

  return columnHeaders.reduce((acc, header, i) => {
    const splittedHeader = header.split(' ');

    if (splittedHeader.some((part) => NO_LINEBREAK_COLUMNS.includes(part.toLowerCase()))) {
      const columnWidth = getColumnMaxWidth(rows, i);

      acc[i] = {
        minCellWidth: columnWidth > maxColWidth ? columnWidth : maxColWidth
      };
    }

    return acc;
  }, {});
};

//generate the page number and date
export const generatePageNumberAndDate = (doc) => {
  const totalPages = doc.internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFontSize(TABLE_FONT_SIZE);
    doc.setTextColor(0, 0, 0);

    const printDate = new Date().toLocaleString();
    const pageNumber = `${i} of ${totalPages}`;

    doc.text(`Print date: ${printDate}`, pageWidth - 60, pageHeight - GAP);
    doc.text(pageNumber, pageWidth / 2, pageHeight - GAP, { align: 'center' });
  }
};

// reformat cells, especially last row with totals
export const formatCells = (data, foot) => {
  if (foot && data.section === 'foot') {
    data.cell.styles.lineWidth = 0.5;
    data.cell.styles.lineColor = [255, 255, 255];

    const applyColor = data.cell.raw;

    data.cell.styles.fillColor = applyColor ? [0, 79, 107] : [255, 255, 255];
    data.cell.styles.textColor = applyColor ? [255, 255, 255] : [0, 0, 0];
  }
};

export const didDrawCell = (data) => {
  const { section, cell, doc } = data;

  if (section === 'body' && typeof cell.raw !== 'string' && !!cell.raw?.strike) {
    const startX = cell.x;
    const endX = startX + doc.getTextWidth(cell.text.join(' ')) + 3;
    const y = cell.y + cell.height / 2;

    doc.setLineWidth(0.2);
    doc.setDrawColor(0);
    doc.line(startX, y, endX, y);
  }
};

export const splitSmartChunks = (array, maxChunkSize = MAX_COLUMNS_PER_PAGE) => {
  const length = array.length;

  // Start with the smallest number of chunks that keeps each chunk ≤ maxChunkSize
  for (let numChunks = 1; numChunks <= length; numChunks++) {
    const chunkSize = Math.ceil(length / numChunks);
    if (chunkSize <= maxChunkSize) {
      const result = [];
      let i = 0;

      for (let c = 0; c < numChunks; c++) {
        const size = Math.ceil((length - i) / (numChunks - c));
        result.push(array.slice(i, i + size));
        i += size;
      }

      return result;
    }
  }

  // Fallback (shouldn't happen unless maxChunkSize < 1)
  return [array];
};

const calculateRowsPerPage = (doc, startY, rowHeight = GAP) => {
  const pageHeight = doc.internal.pageSize.height;

  const usableHeight = pageHeight - startY;

  return Math.floor(usableHeight / rowHeight);
};

const estimateRowHeight = (doc, rows) => {
  const pageWidth = doc.internal.pageSize.width;

  const heights = rows.flatMap((row) => {
    const rowWidth = row.reduce((sum, cellText) => sum + (String(cellText ?? '')?.length || 0), 0);

    const lines = Math.floor(rowWidth / pageWidth) + 1;

    return lines * TABLE_FONT_SIZE;
  });

  const uniqueHeights = [...new Set(heights)];

  const avg = uniqueHeights.reduce((sum, h) => sum + h, 0) / uniqueHeights.length; // avg of heights

  return Math.floor(avg);
};

export const splitRowsChunk = ({ doc, startY, rows }) => {
  const rowHeight = estimateRowHeight(doc, rows);

  const firstPageRows = calculateRowsPerPage(doc, startY, rowHeight);
  const rowsPerPage = calculateRowsPerPage(doc, 0, rowHeight);

  const chunkedRows = [];

  chunkedRows.push(rows.slice(0, firstPageRows));

  for (let i = firstPageRows; i < rows.length - firstPageRows; i += rowsPerPage) {
    chunkedRows.push(rows.slice(i, i + rowsPerPage));
  }
  return chunkedRows;
};

export const updateFootPosition = (totalsSum = [], headerChunk = []) => {
  const updatedFoot = headerChunk.slice(totalsSum?.[0]?.length).map((_) => '');

  return [
    headerChunk.map((_) => ''),
    ...(totalsSum ? totalsSum.map((foot) => [...updatedFoot, ...foot]) : [])
  ];
};
